"use client"

import { useState, useEffect } from "react"
import { useLanguage } from "@/lib/language-context"
import { Link } from "react-router-dom"
import { Heart, ShoppingBag } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

interface FavoriteItem {
  id: number
  name: string
  price: number
  image: string
  slug: string
}

export function FavoritesContent() {
  const { t } = useLanguage()
  const [favorites, setFavorites] = useState<FavoriteItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Load favorites from localStorage
    setLoading(true)
    const stored = localStorage.getItem("favorites")
    if (stored) {
      try {
        setFavorites(JSON.parse(stored))
      } catch {
        setFavorites([])
      }
    }
    setLoading(false)
  }, [])

  const removeFavorite = (id: number) => {
    const updated = favorites.filter((item) => item.id !== id)
    setFavorites(updated)
    localStorage.setItem("favorites", JSON.stringify(updated))
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  if (favorites.length === 0) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <Heart className="h-20 w-20 mx-auto text-muted-foreground mb-4 opacity-50" />
            <h1 className="text-3xl font-bold mb-4">{t("favorites", "empty_title") || "Sevimlilar bo'sh"}</h1>
            <p className="text-muted-foreground mb-8">
              {t("favorites", "empty_message") || "Hali sevimli mahsulotlar qo'shmagansiz"}
            </p>
            <Link to="/products">
              <Button size="lg" className="bg-green-600 hover:bg-green-700">
                {t("nav", "products") || "Mahsulotlar"}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">{t("favorites", "title") || "Sevimlilar"}</h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {favorites.map((item) => (
            <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative aspect-square bg-gray-100 overflow-hidden group">
                <img
                  src={item.image || "/placeholder.svg"}
                  alt={item.name}
                  className="object-cover group-hover:scale-110 transition-transform duration-300 w-full h-full"
                />

                {/* Remove Button */}
                <button
                  onClick={() => removeFavorite(item.id)}
                  className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors z-10"
                >
                  <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                </button>
              </div>

              <CardContent className="p-4">
                <Link to={`/products/${item.slug}`}>
                  <h3 className="font-semibold line-clamp-2 hover:text-green-600 transition-colors mb-2">
                    {item.name}
                  </h3>
                </Link>

                <div className="flex items-center justify-between">
                  <p className="text-green-600 font-bold">
                    {item.price.toLocaleString()} {t("products", "currency") || "so'm"}
                  </p>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0 bg-transparent">
                    <ShoppingBag className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
